import React from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import ServiceList from "./components/general/ServiceList";
import InvoiceForm from "./components/invoices/InvoiceForm";
import Invoice from "./components/invoices/Invoice";
import { InvoiceFormProvider } from "./contexts/InvoiceFormContext";
import { useAuth } from "./contexts/AuthContext";
import AuthPage from "./components/general/AuthPage";
import UserManagementPage from "./components/users/UserManagementPage";
import Invoices from "./components/invoices/Invoices";
import SubscriptionQCList from "./components/subscription-qc/SubscriptionQCs"; // Import the SubscriptionQC list page
import SubscriptionQC from "./components/subscription-qc/SubscriptionQC"; // Import the SubscriptionQC detail page
import RecurringInvoiceQC from "./components/recurring-invoice-qc/RecurringInvoiceQC";
import RecurringInvoiceQCs from "./components/recurring-invoice-qc/RecurringInvoiceQCs";
import ExceptionInvoice from "./components/exception-invoices/ExceptionInvoice";
import ExceptionInvoices from "./components/exception-invoices/ExceptionInvoices";
import ExceptionInvoicesForm from "./components/exception-invoices/ExceptionInvoicesForm";
import CreditMemoSend from "./components/credit-memo-send/CreditMemoSend";
import CreditMemoSends from "./components/credit-memo-send/CreditMemoSends";
import CreditMemoSendForm from "./components/credit-memo-send/CreditMemoSendForm";
import JournalApproval from "./components/journal-approval/JournalApproval";
import JournalApprovals from "./components/journal-approval/JournalApprovals";
import JournalApprovalForm from "./components/journal-approval/JournalApprovalForm";
import PoNumberReissueForm from "./components/po-number-reissue/PoReissueForm";
import PoReissue from "./components/po-number-reissue/PoReissue";
import PoReissues from "./components/po-number-reissue/PoReissues";
import SubscriptionTransferForm from "./components/subscription-transfer/subscriptionTransferForm";
import SubscriptionTransfers from "./components/subscription-transfer/subscriptionTransfers";
import { SubTransferFormProvider } from "./contexts/SubscriptionTransferFormContext";
import SubscriptionTransfer from "./components/subscription-transfer/subscriptionTransfer";
import PoClosures from "./components/po-closure/PoClosures";
import PoClosureForm from "./components/po-closure/PoClosureForm";
import PoClosure from "./components/po-closure/PoClosure";
import AcquisitionImports from "./components/acquisition-import/AcquisitionImports";
import AcquisitionImport from "./components/acquisition-import/AcquisitionImport";
import AcquisitionImportRun from "./components/acquisition-import/AcquisitionImportRun";
import { isAllowedPoClosureRequesterEmail } from "./util/helpers";

const AppRouter: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const canUsePoClosureActions = isAllowedPoClosureRequesterEmail(user?.email);

  return (
    <Router>
      {!isAuthenticated ? (
        <Routes>
          <Route path="/" element={<AuthPage />} />
          <Route path="/*" element={<AuthPage />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/" element={<ServiceList />} />

          {/* User Management Routes */}
          <Route path="/users" element={<UserManagementPage />} />

          {/* Invoice Routes */}
          <Route path="/invoices" element={<Invoices />} />
          <Route
            path="/invoices/ps-new-biz-form"
            element={
              <InvoiceFormProvider>
                <InvoiceForm />
              </InvoiceFormProvider>
            }
          />
          <Route path="/invoices/:id" element={<Invoice />} />

          {/* Subscription Transfer Routes */}
          <Route
            path="/subscription-transfers"
            element={<SubscriptionTransfers />}
          />
          <Route
            path="/subscription-transfers/ps-new-biz-form"
            element={
              <SubTransferFormProvider>
                <SubscriptionTransferForm />
              </SubTransferFormProvider>
            }
          />
          <Route path="/subscription-transfers/:id" element={<SubscriptionTransfer />} />
          
          {/* Subscription QC Routes */}
          <Route path="/subscription-qc" element={<SubscriptionQCList />} />
          <Route path="/subscription-qc/:id" element={<SubscriptionQC />} />

          {/* Recurring Invoice QC Routes */}
          <Route
            path="/recurring-invoice-qc"
            element={<RecurringInvoiceQCs />}
          />
          <Route
            path="/recurring-invoice-qc/:id"
            element={<RecurringInvoiceQC />}
          />

          {/* Invoice Exception Routes */}
          <Route path="/exception-invoices" element={<ExceptionInvoices />} />
          <Route
            path="/exception-invoices/:id"
            element={<ExceptionInvoice />}
          />
          <Route
            path="/invoice-exceptions/form"
            element={<ExceptionInvoicesForm />}
          />

          <Route path="/credit-memos" element={<CreditMemoSends />} />
          <Route path="/credit-memos/form" element={<CreditMemoSendForm />} />
          <Route path="/credit-memos/:id" element={<CreditMemoSend />} />

          <Route path="/journal-approvals" element={<JournalApprovals />} />
          <Route
            path="/journal-approvals/form"
            element={<JournalApprovalForm />}
          />
          <Route path="/journal-approvals/:id" element={<JournalApproval />} />

          {/* PO Inv Reissue Routes */}
          <Route path="/po-reissues" element={<PoReissues />} />
          <Route path="/po-reissues/form" element={<PoNumberReissueForm />} />
          <Route path="/po-reissues/:id" element={<PoReissue />} />

          {/* PO Closure Routes */}
          <Route path="/po-closures" element={<PoClosures />} />
          <Route
            path="/po-closures/form"
            element={
              canUsePoClosureActions ? (
                <PoClosureForm />
              ) : (
                <Navigate to="/po-closures" replace />
              )
            }
          />
          <Route path="/po-closures/:id" element={<PoClosure />} />

          <Route path="/acquisition-imports" element={<AcquisitionImports />} />
          <Route
            path="/acquisition-imports/:acquisitionId"
            element={<AcquisitionImport />}
          />
          <Route
            path="/acquisition-imports/:acquisitionId/runs/:runId"
            element={<AcquisitionImportRun />}
          />
        </Routes>
      )}
    </Router>
  );
};

export default AppRouter;
